export interface PlatformRequirement {
  id: string;
  name: string;
  maxVideoDuration: number; // in seconds
  maxFileSize: number; // in bytes
  maxCharacters?: number; // Maximum characters for text posts
  maxImages: number; // Maximum number of images per post
  maxVideos: number; // Maximum number of videos per post
  allowMixedMedia: boolean; // Can mix images and videos in same post
  allowedFormats?: string[];
  notes?: string;
}

export const PLATFORM_REQUIREMENTS: PlatformRequirement[] = [
  {
    id: 'x',
    name: 'X (Twitter)',
    maxVideoDuration: 140, // 2 minutes 20 seconds
    maxFileSize: 1024 * 1024 * 1024, // 1 GB
    maxCharacters: 280,
    maxImages: 4,
    maxVideos: 4, // Up to 4 videos per post (since 2024)
    allowMixedMedia: true, // Can mix images and videos
    notes: 'Videos longer than 140 seconds may be rejected. Max 4 media items per post (images, videos, or mix)',
  },
  {
    id: 'bluesky',
    name: 'Bluesky',
    maxVideoDuration: 180, // 3 minutes
    maxFileSize: 100 * 1024 * 1024, // 100 MB
    maxCharacters: 300,
    maxImages: 4,
    maxVideos: 1,
    allowMixedMedia: false,
    notes: 'Carousel: static images only (no GIFs, no videos). Max 4 images. Single video or single image allowed.',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    maxVideoDuration: 60, // 1 minute for feed, 90s for reels
    maxFileSize: 100 * 1024 * 1024, // 100 MB
    maxCharacters: 2200,
    maxImages: 10,
    maxVideos: 1,
    allowMixedMedia: false, // Instagram does NOT support mixed media in carousels
    notes: 'Feed videos: 60s, Reels: 90s. Carousels: images only (max 10). Videos must be posted separately',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    maxVideoDuration: 600, // 10 minutes
    maxFileSize: 50 * 1024 * 1024, // 50 MB (limit for single-chunk upload)
    maxCharacters: 2200,
    maxImages: 0,
    maxVideos: 1,
    allowMixedMedia: false,
    notes: 'Maximum 10 minutes and 50MB (single-chunk). Video only',
  },
  {
    id: 'threads',
    name: 'Threads',
    maxVideoDuration: 300, // 5 minutes
    maxFileSize: 100 * 1024 * 1024, // 100 MB
    maxCharacters: 500,
    maxImages: 20,
    maxVideos: 1,
    allowMixedMedia: true,
    notes: 'Maximum 5 minutes. Up to 20 items in carousel',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    maxVideoDuration: 3600, // 60 minutes (unverified accounts)
    maxFileSize: 128 * 1024 * 1024 * 1024, // 128 GB
    maxCharacters: 5000,
    maxImages: 0,
    maxVideos: 1,
    allowMixedMedia: false,
    notes: 'Varies by account verification status. Single video only',
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    maxVideoDuration: 15 * 60, // 15 minutes
    maxFileSize: 2 * 1024 * 1024 * 1024, // 2 GB
    maxCharacters: 500,
    maxImages: 1,
    maxVideos: 1,
    allowMixedMedia: false,
    notes: 'Maximum 15 minutes. Single media per pin',
  },
  {
    id: 'tumblr',
    name: 'Tumblr',
    maxVideoDuration: 300, // 5 minutes (standard limit)
    maxFileSize: 100 * 1024 * 1024, // 100 MB
    maxCharacters: 4096, // Tumblr has very high character limits
    maxImages: 10,
    maxVideos: 1,
    allowMixedMedia: true,
    notes: 'Maximum 5 minutes and 100MB. Up to 10 images per post',
  },
  {
    id: 'discord',
    name: 'Discord',
    maxVideoDuration: 3600, // 1 hour (virtually no limit other than file size)
    maxFileSize: 10 * 1024 * 1024, // 10 MB (free tier limit, reduced Sep 2024)
    maxCharacters: 2000,
    maxImages: 10,
    maxVideos: 10, // Up to 10 attachments including multiple videos
    allowMixedMedia: true,
    notes: 'Maximum 2000 characters. File size limit depends on server level. Up to 10 attachments',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    maxVideoDuration: 3600, // No real limit for bots
    maxFileSize: 50 * 1024 * 1024, // 50 MB (Bot API limit)
    maxCharacters: 4096,
    maxImages: 10,
    maxVideos: 10, // Media groups support multiple videos
    allowMixedMedia: true,
    notes: 'Maximum 4096 characters and 50MB per file. Media groups up to 10 items',
  },
];

export function getPlatformRequirement(platformId: string): PlatformRequirement | undefined {
  // twitter, x-es (Spanish X account) all use the same requirements as x
  if (platformId === 'x-es' || platformId === 'twitter') {
    return PLATFORM_REQUIREMENTS.find(p => p.id === 'x');
  }
  return PLATFORM_REQUIREMENTS.find(p => p.id === platformId);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Calculate the strictest media limits across selected platforms
 */
export function calculateStrictestLimits(platformIds: string[]): {
  maxImages: number;
  maxVideos: number;
  allowMixedMedia: boolean;
  maxVideoDuration: number;
  maxFileSize: number;
} {
  if (platformIds.length === 0) {
    return {
      maxImages: 1,
      maxVideos: 1,
      allowMixedMedia: false,
      maxVideoDuration: 60,
      maxFileSize: 25 * 1024 * 1024,
    };
  }

  const requirements = platformIds
    .map(id => getPlatformRequirement(id))
    .filter((req): req is PlatformRequirement => req !== undefined);

  if (requirements.length === 0) {
    return {
      maxImages: 1,
      maxVideos: 1,
      allowMixedMedia: false,
      maxVideoDuration: 60,
      maxFileSize: 25 * 1024 * 1024,
    };
  }

  return {
    maxImages: Math.min(...requirements.map(r => r.maxImages)),
    maxVideos: Math.min(...requirements.map(r => r.maxVideos)),
    allowMixedMedia: requirements.every(r => r.allowMixedMedia),
    maxVideoDuration: Math.min(...requirements.map(r => r.maxVideoDuration)),
    maxFileSize: Math.min(...requirements.map(r => r.maxFileSize)),
  };
}

/**
 * Validate media items against platform limits
 */
export function validateMediaAgainstPlatforms(
  mediaItems: Array<{ type: 'image' | 'video' }>,
  platformIds: string[]
): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const limits = calculateStrictestLimits(platformIds);

  const imageCount = mediaItems.filter(m => m.type === 'image').length;
  const videoCount = mediaItems.filter(m => m.type === 'video').length;

  if (imageCount > limits.maxImages) {
    errors.push(`Too many images: ${imageCount}/${limits.maxImages} allowed`);
  }

  if (videoCount > limits.maxVideos) {
    errors.push(`Too many videos: ${videoCount}/${limits.maxVideos} allowed`);
  }

  if (imageCount > 0 && videoCount > 0 && !limits.allowMixedMedia) {
    errors.push('Selected platforms do not support mixing images and videos');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/** Detect if path or filename indicates a GIF (Bluesky carousel does not support GIFs) */
function isGifPath(pathOrFilename: string | null | undefined): boolean {
  if (!pathOrFilename) return false;
  return pathOrFilename.toLowerCase().endsWith('.gif');
}

export type MediaItemForValidation = { type: 'image' | 'video'; path?: string | null; fileName?: string | null };

function itemIsGif(item: MediaItemForValidation): boolean {
  return isGifPath(item.path ?? item.fileName ?? undefined);
}

/**
 * For a given media item at a specific position, return per-platform warnings
 * about which platforms will NOT receive this item due to their limits.
 * Special handling for Instagram's order-dependent restrictions and Bluesky (images only, no GIF).
 */
export function getItemPlatformWarnings(
  itemIndex: number,
  itemType: 'image' | 'video',
  allItems: Array<MediaItemForValidation>,
  platformIds: string[]
): { excluded: string[]; message: string; isBlocking: boolean } | null {
  if (platformIds.length === 0) return null;

  const currentItem = allItems[itemIndex];

  // Count how many items of this type come before (and including) this position
  let sameTypeIndex = 0;
  for (let i = 0; i <= itemIndex; i++) {
    if (allItems[i].type === itemType) sameTypeIndex++;
  }

  const excluded: string[] = [];
  let hasBlockingIssue = false;

  for (const platformId of platformIds) {
    const req = getPlatformRequirement(platformId);
    if (!req) continue;

    // SPECIAL CASE: Bluesky carousel = static images only (no video, no GIF), max 4
    if (platformId === 'bluesky' && allItems.length > 1) {
      if (itemType === 'video') {
        excluded.push(req.name);
        hasBlockingIssue = true;
        continue;
      }
      if (currentItem && itemIsGif(currentItem)) {
        excluded.push(req.name);
        hasBlockingIssue = true;
        continue;
      }
      if (sameTypeIndex > req.maxImages) {
        excluded.push(req.name);
        continue;
      }
      continue;
    }

    // SPECIAL CASE: Instagram order-dependent validation
    if (platformId === 'instagram' && allItems.length > 1) {
      const firstItem = allItems[0];
      
      // If first item is an image and this is a video (at any position > 0)
      if (firstItem.type === 'image' && itemType === 'video') {
        excluded.push(req.name);
        hasBlockingIssue = true;
        continue;
      }
      
      // If first item is a video and this is not the first item
      if (firstItem.type === 'video' && itemIndex > 0) {
        excluded.push(req.name);
        hasBlockingIssue = true;
        continue;
      }
    }

    const maxForType = itemType === 'video' ? req.maxVideos : req.maxImages;

    // If this platform doesn't support this type at all
    if (maxForType === 0) {
      excluded.push(req.name);
      hasBlockingIssue = true;
      continue;
    }

    // If this item's position exceeds the platform's limit for this type
    if (sameTypeIndex > maxForType) {
      excluded.push(req.name);
      continue;
    }

    // Check mixed media: if the platform doesn't allow it and there are both types
    if (!req.allowMixedMedia) {
      const hasOtherType = allItems.some(m => m.type !== itemType);
      if (hasOtherType) {
        excluded.push(req.name);
        hasBlockingIssue = true;
      }
    }
  }

  if (excluded.length === 0) return null;

  const message = excluded.length === platformIds.length
    ? `Will not be uploaded: incompatible with all selected platforms`
    : `Will not be uploaded to: ${excluded.join(', ')}`;

  return { excluded, message, isBlocking: hasBlockingIssue };
}

/**
 * Check if the current media configuration has any blocking issues
 * that should prevent publishing (e.g., all items excluded for a platform)
 * Items may include optional path for GIF detection (Bluesky).
 */
export function hasBlockingMediaIssues(
  allItems: Array<MediaItemForValidation>,
  platformIds: string[]
): { hasIssues: boolean; message: string } {
  if (allItems.length === 0 || platformIds.length === 0) {
    return { hasIssues: false, message: '' };
  }

  // Check for Bluesky-specific blocking: carousel = static images only (no GIF, no video), max 4
  if (platformIds.includes('bluesky') && allItems.length > 1) {
    const hasVideo = allItems.some(m => m.type === 'video');
    const hasGif = allItems.some(m => itemIsGif(m));

    if (hasVideo) {
      return {
        hasIssues: true,
        message: 'Bluesky: Carousel allows only static images (no videos). Use a single video or up to 4 images only.',
      };
    }
    if (hasGif) {
      return {
        hasIssues: true,
        message: 'Bluesky: Carousel allows only static images (no GIFs). Use up to 4 JPEG/PNG images only.',
      };
    }
    if (allItems.length > 4) {
      return {
        hasIssues: true,
        message: 'Bluesky: Maximum 4 images per post. Remove extra media or deselect Bluesky.',
      };
    }
  }

  // Check for Instagram-specific blocking issues
  if (platformIds.includes('instagram') && allItems.length > 1) {
    const firstItem = allItems[0];
    const hasVideosAfterImage = firstItem.type === 'image' && allItems.some((item, idx) => idx > 0 && item.type === 'video');
    const hasItemsAfterVideo = firstItem.type === 'video' && allItems.length > 1;
    
    if (hasVideosAfterImage) {
      return { 
        hasIssues: true, 
        message: 'Instagram: Videos after images will not be uploaded (carousels are images-only)' 
      };
    }
    
    if (hasItemsAfterVideo) {
      return { 
        hasIssues: true, 
        message: 'Instagram: Only the first video will be uploaded (cannot mix multiple items with video)' 
      };
    }
  }

  // Check if any platform has mixed media when not allowed
  for (const platformId of platformIds) {
    const req = getPlatformRequirement(platformId);
    if (!req) continue;

    const videoCount = allItems.filter(m => m.type === 'video').length;
    const imageCount = allItems.filter(m => m.type === 'image').length;

    if (!req.allowMixedMedia && videoCount > 0 && imageCount > 0) {
      return { 
        hasIssues: true, 
        message: `${req.name} does not support mixing images and videos` 
      };
    }

    if (videoCount > req.maxVideos) {
      return { 
        hasIssues: true, 
        message: `${req.name} allows max ${req.maxVideos} video(s), but you have ${videoCount}` 
      };
    }

    if (imageCount > req.maxImages) {
      return { 
        hasIssues: true, 
        message: `${req.name} allows max ${req.maxImages} image(s), but you have ${imageCount}` 
      };
    }
  }

  return { hasIssues: false, message: '' };
}

